import { queryOptions } from "@tanstack/react-query";
import { fetchActiveTasks } from "@/features/task-progress/api/task-progress.api";
import type { TaskRecord } from "@nexus/contracts";

// ─── Key Factory ────────────────────────────────────────────────────

export const taskProgressKeys = {
  all: ["task-progress"] as const,
  active: (userEmail: string) => ["task-progress", "active", userEmail] as const,
};

// ─── Helpers ────────────────────────────────────────────────────────

// Poll every 500ms while tasks are active. With 250ms worker ticks and a 2s phase
// minimum, each poll catches ~2 new tick values — giving the user 4 visible states
// per phase (≈ 25% → 50% → 75% → 100%) without excessive server chatter.
const TASK_POLL_INTERVAL = 500;
const TASK_DISCOVERY_INTERVAL = 5_000;

function hasActiveTasks(tasks: TaskRecord[] | undefined): boolean {
  if (!tasks || tasks.length === 0) return false;
  return tasks.some((task) =>
    task.stages.some((stage) =>
      stage.subtasks.some((s) => s.progress < 1),
    ),
  );
}

// ─── Query Options ──────────────────────────────────────────────────

export function activeTasksQueryOptions(userEmail: string | undefined) {
  return queryOptions({
    queryKey: taskProgressKeys.active(userEmail ?? ""),
    queryFn: fetchActiveTasks,
    enabled: !!userEmail,
    refetchInterval: (query) => {
      // Fast poll while any task is actively in-progress (capturing tick updates).
      if (hasActiveTasks(query.state.data)) return TASK_POLL_INTERVAL;
      // Slow poll when all tasks are terminal (done/failed) or the list is empty —
      // terminal cards don't change, so 500ms is unnecessary while the user decides
      // whether to dismiss.
      return TASK_DISCOVERY_INTERVAL;
    },
  });
}
